import React, { useCallback } from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';
import { Pressable, StyleSheet } from 'react-native';

import { BodyText, theme, useResponsive } from '@sg/garnish';

import { type HomeProductCategory } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeFilterChip = (props: HomeFilterChipProps) => {
  const { isSelected, category, onPress } = props;
  const { match } = useResponsive();

  // ─── Messages ──────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const a11yLabel = formatMessage(messages.a11yLabel, { filter: category.categoryName });

  // ─── Styles ────────────────────────────────────────────────────────────────

  const responsiveContainer = match([styles.containerXS, styles.containerSM]);
  const containerStyle = [responsiveContainer, isSelected ? styles.selectedContainer : null];
  const textStyle = isSelected ? styles.selectedText : match([null, styles.textSM]);

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress(isSelected ? null : category);
  }, [category, isSelected, onPress]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      role="radio"
      aria-label={a11yLabel}
      aria-selected={isSelected}
      style={containerStyle}
      onPress={handleOnPress}
    >
      <BodyText style={textStyle} sizeMatch={['14', '18']}>
        {category.categoryName}
      </BodyText>
    </Pressable>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 32,
    borderRadius: 64,
    paddingVertical: theme.spacing['2'],
    paddingHorizontal: theme.spacing['4'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  containerSM: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    borderRadius: 64,
    paddingHorizontal: theme.spacing['8'],
    backgroundColor: theme.colors.OPACITY.SPINACH.NEAR_LIGHTEST,
  },
  textSM: {
    color: theme.colors.KALE,
  },
  selectedContainer: {
    backgroundColor: theme.colors.SPINACH,
  },
  selectedText: {
    color: theme.colors.WHITE,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{ readonly a11yLabel: { readonly filter: MessageValue } }>({
  a11yLabel: {
    defaultMessage: 'Select {filter}',
    description: 'Home | Filter Chip | A11y Label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeFilterChipProps = {
  isSelected: boolean;
  category: HomeProductCategory;
  onPress: (category: HomeProductCategory | null) => void;
};
